import { getOperationsDelta } from '@/shared/api';
import { OPERATIONS_HISTORY_KEY } from '@/shared/config';
import { useAppSettingsStore } from '@/shared/lib';
import type { OperationsListResponseDto } from '@/shared/types';
import type { QueryClient } from '@tanstack/react-query';
import { mergeOperations } from './mergeOperations';

const lastUpdateByKey = new Map<string, number>();
const inFlightByKey = new Map<string, Promise<boolean>>();

type SyncOptions = {
  skipIfBusy?: boolean;
};

const advanceLastUpdate = (signature: string, nextFromMs: number) => {
  const current = lastUpdateByKey.get(signature);

  if (current == null || nextFromMs > current) {
    lastUpdateByKey.set(signature, nextFromMs);
  }
};

const requestOperationsDelta = async (
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  signature: string
): Promise<boolean> => {
  const filter = String(queryKey[3] ?? '');
  const queryString = String(queryKey[4] ?? '');

  const toMs = Date.now();

  let fromMs = lastUpdateByKey.get(signature);
  if (fromMs == null) {
    const cached =
      queryClient.getQueryData<OperationsListResponseDto>(queryKey);
    const dataUpdatedAt = queryClient.getQueryState(queryKey)?.dataUpdatedAt;
    fromMs = cached?.serverDateTime || dataUpdatedAt || toMs;
  }

  const datetimefrom = Math.floor(fromMs / 1000);
  const datetimeto = Math.floor(toMs / 1000);

  const prefix = queryString ? `${queryString}&` : '';
  const data = await getOperationsDelta(
    `${filter}?${prefix}datetimefrom=${datetimefrom}&datetimeto=${datetimeto}`
  );

  const serverDateTime = data?.serverDateTime;
  const nextFromMs =
    typeof serverDateTime === 'number' && serverDateTime > 0
      ? Math.min(toMs, serverDateTime)
      : toMs;

  if (!data?.operations?.length) {
    advanceLastUpdate(signature, nextFromMs);
    return false;
  }

  const existing =
    queryClient.getQueryData<OperationsListResponseDto>(queryKey);

  let hasChanges = true;

  if (existing) {
    const { operations, changed } = mergeOperations(
      existing.operations,
      data.operations
    );
    hasChanges = changed;
    queryClient.setQueryData<OperationsListResponseDto>(queryKey, {
      ...existing,
      serverDateTime: data.serverDateTime ?? existing.serverDateTime,
      operations,
    });
  } else {
    queryClient.setQueryData<OperationsListResponseDto>(queryKey, data);
  }

  advanceLastUpdate(signature, nextFromMs);

  return hasChanges;
};

const syncSingleOperationsDelta = (
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  { skipIfBusy }: SyncOptions = {}
): Promise<boolean> => {
  const filter = String(queryKey[3] ?? '');
  if (!filter) return Promise.resolve(false);

  const signature = JSON.stringify(queryKey);
  const inFlight = inFlightByKey.get(signature);

  if (inFlight && skipIfBusy) return Promise.resolve(false);

  const run = () => requestOperationsDelta(queryClient, queryKey, signature);

  const request: Promise<boolean> = (
    inFlight ? inFlight.catch(() => false).then(run) : run()
  ).finally(() => {
    if (inFlightByKey.get(signature) === request) {
      inFlightByKey.delete(signature);
    }
  });

  inFlightByKey.set(signature, request);

  return request;
};

export const syncActiveOperationsDeltas = (
  queryClient: QueryClient,
  userId?: string,
  options: SyncOptions = {}
): Promise<boolean> => {
  if (!userId) return Promise.resolve(false);

  const language = useAppSettingsStore.getState().language;
  const canonicalKey = [
    OPERATIONS_HISTORY_KEY,
    userId,
    language,
    'all',
    '',
  ] as const;

  return syncSingleOperationsDelta(queryClient, canonicalKey, options).catch(
    () => false
  );
};
